/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Retention (contract section 5): weekly cohorts (Monday weeks, UTC) and the share of each that came back in the
 * weeks after, as a heat table, from GET /v1/admin/usage/retention?weeks= with the lens (4, 8, 12 or 26 weeks). The first
 * cohort includes everyone who was already using Gyro when collection began, and the table says so.
 */
import { useState } from 'react';
import { Repeat } from 'lucide-react';
import { api } from '../../lib/api';
import { DEFAULT_RETENTION_WEEKS, RETENTION_WEEKS, heatStep, normalizeRetention, weekLabel, type Cohort, type RetentionWeeks } from '../../lib/usage';
import { Callout, EmptyState, Panel, Segmented, Table, Tag, type Column } from '../../ui';
import { count, percent } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { UsageError, type SectionProps } from './parts';

const WEEK_OPTIONS = RETENTION_WEEKS.map((weeks) => ({ value: String(weeks), label: `${weeks} weeks` }));

export function UsageRetention({ query, lensKey }: SectionProps) {
  const [weeks, setWeeks] = useState<RetentionWeeks>(DEFAULT_RETENTION_WEEKS);
  const { load, reload, reloading } = useLoad(() => api.usageRetention({ ...query, weeks }).then((wire) => normalizeRetention(wire, weeks)), `retention|${lensKey}|${weeks}`);
  const data = load.state === 'ready' ? load.data : null;
  const shown = data?.weeks ?? weeks;
  const columns: Array<Column<Cohort>> = [
    { key: 'week', header: 'Cohort', cell: (row) => <span className="ui-cell-stack"><span>{weekLabel(row.week)}</span>{row.includesExisting ? <span><Tag mono={false}>includes existing users</Tag></span> : null}</span> },
    { key: 'users', header: 'Users', numeric: true, cell: (row) => count(row.users) },
    // W0 is the cohort's own week (everyone in it was seen, so it reads 100%); W1 the week after, and so on.
    ...Array.from({ length: shown }, (_, index) => ({
      key: `w${index}`,
      header: `W${index}`,
      numeric: true,
      hideOnPhone: index >= 5,
      cell: (row: Cohort) => {
        const rate = row.rates[index] ?? null;
        const title = rate === null ? 'Not reached yet' : index === 0 ? `${percent(rate, 0)} of the cohort was seen in its own week` : `${percent(rate, 0)} of the cohort came back ${index === 1 ? '1 week' : `${index} weeks`} later`;
        return <span className={`usage-heat is-${heatStep(rate)}`} title={title}>{rate === null ? '—' : percent(rate, 0)}</span>;
      },
    })),
  ];

  return (
    <Panel
      flush
      title="Retention"
      description="Each row is the people whose first week was that week; W0 is that week itself, and each later cell is the share of them seen again that many weeks later"
      actions={<Segmented label="Weeks" options={WEEK_OPTIONS} value={String(weeks)} onChange={(value) => setWeeks(Number(value) as RetentionWeeks)} />}
      footer={<span>The first cohort includes existing users: everyone already using Gyro when collection began counts as new in that week, so its return rate reads high. Weeks are Monday to Sunday, UTC.</span>}
    >
      {load.state === 'failed' ? <div className="ui-panel-pad"><UsageError title="Retention could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></div> : (
        <>
          {data?.cohorts.some((cohort) => cohort.includesExisting) ? (
            <div className="ui-panel-pad ui-panel-pad--divided">
              <Callout tone="info" title="The first cohort includes existing users">People who used Gyro before collection began all count as new in the first week, so that row is not a true cohort.</Callout>
            </div>
          ) : null}
          <Table
            label="Retention cohorts"
            className="ui-table--wrap"
            columns={columns}
            rows={data?.cohorts ?? []}
            rowKey={(row) => row.week}
            loading={load.state === 'loading'}
            skeletonRows={4}
            stickyHeader={false}
            empty={<EmptyState compact icon={Repeat} title="No cohorts yet">Cohorts appear a week after the first events are collected; the lens above may be leaving people out.</EmptyState>}
          />
        </>
      )}
    </Panel>
  );
}
